import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const empty: ProfileData = { first_name: "", last_name: "", email: "", phone: "" };

export function useProfile(userId: string | undefined, userEmail?: string | null) {
  const [profile, setProfile] = useState<ProfileData>(empty);
  // Start in `loading=true` whenever there's a user, so the UI can render a
  // "Cargando perfil…" indicator on first render — before the effect kicks in.
  const [loading, setLoading] = useState(!!userId);
  const [saving, setSaving] = useState(false);

  const refetch = useCallback(async () => {
    if (!userId) { setProfile(empty); setLoading(false); return null; }
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("first_name,last_name,email,phone")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) console.error("useProfile load error:", error);
    const next: ProfileData = {
      first_name: data?.first_name ?? "",
      last_name: data?.last_name ?? "",
      email: data?.email ?? userEmail ?? "",
      phone: data?.phone ?? "",
    };
    setProfile(next);
    setLoading(false);
    return next;
  }, [userId, userEmail]);

  useEffect(() => {
    let cancelled = false;
    (async () => { const r = await refetch(); if (cancelled) void r; })();
    return () => { cancelled = true; };
  }, [refetch]);

  const save = useCallback(async (next: ProfileData) => {
    if (!userId) return { error: new Error("not_authenticated") };
    setSaving(true);
    // Defense-in-depth: re-check auth.uid() on the client matches the userId
    // we're about to write. RLS already enforces this, but bailing out early
    // gives us a clearer error and avoids a wasted round-trip.
    const { data: authData } = await supabase.auth.getUser();
    const authUid = authData?.user?.id;
    console.info("[useProfile.save] request", { userId, authUid });
    if (!authUid || authUid !== userId) {
      setSaving(false);
      console.warn("[useProfile.save] aborted: auth.uid mismatch", { userId, authUid });
      return { error: new Error("auth_uid_mismatch") };
    }
    const { error, count } = await supabase
      .from("profiles")
      .update(
        {
          first_name: next.first_name || null,
          last_name: next.last_name || null,
          email: next.email || null,
          phone: next.phone || null,
          updated_at: new Date().toISOString(),
        },
        { count: "exact" }
      )
      .eq("user_id", userId);
    console.info("[useProfile.save] result", { userId, authUid, count, error });
    if (!error) {
      // Re-read from DB so the UI reflects the persisted row (incl. server-side
      // normalization like trimming/casing) instead of the optimistic draft.
      await refetch();
    }
    setSaving(false);
    return { error };
  }, [userId, refetch]);

  return { profile, setProfile, save, refetch, loading, saving };
}
